import { sleep } from '@flowus/common/async';
import { Login, TeamInviteWeb, ViewType } from '@flowus/login';
import { JoinSpaceError } from '@flowus/login/component/common/join-space-error';
import { VITE_PRODUCT_NAME } from '@flowus/login/const';
import { clearPromotionChannel, getPromotionChannel } from '@flowus/login/hook/promotion-channel';
import { TeamInviteStatus } from '@flowus/login/hook/use-team-invite';
import { isNumber } from 'lodash-es';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { LoadingContainer } from 'src/common/components/loading-container';
import { message } from 'src/common/components/message';
import { USER_ID } from 'src/common/const';
import { request } from 'src/common/request';
import { getUrlWithoutDomain } from 'src/common/utils/url-utils';
import { Container } from 'src/components/login-uikit/container';
import { PRODUCT_NAME } from 'src/const/title';
import { isBuildIn } from 'src/env';
import { kolActivity } from 'src/hooks/activities/common';
import { useGetQuery } from 'src/hooks/page/use-get-query';
import { useJumpQueryCallback } from 'src/hooks/public/use-jump-query-callback';
import { fetchSpaces } from 'src/hooks/space';
import { switchSpaceViewIdBySpaceId } from 'src/hooks/user/switch-space-view-id';
import { updateCurrentUser } from 'src/services/user/current-user';
import { SearchParams, ViewPath } from 'src/utils';
import { setLocalStorage } from 'src/utils/local-storage';
import { locationToUrl } from 'src/utils/location-utils';

export const LoginWrapper: FC = () => {
  const getQuery = useGetQuery();
  const [isLoading, setIsLoading] = useState(false);
  const urlInviteCode = getQuery(SearchParams.inviteCode);
  const pageCode = getQuery(SearchParams.page);
  const inviteTeamCode = getQuery(SearchParams.inviteTeam);
  const cooperateCode = getQuery(SearchParams.cooperate);
  const quickRegister = getQuery(SearchParams.quickRegister, 'false') === 'true';
  const jumpQueryCallback = useJumpQueryCallback();
  // 记录当前到哪一个步骤了
  const [viewType, setViewType] = useState<ViewType>();
  const hideFooter = Boolean(
    viewType && [ViewType.FORGET_PASSWORD, ViewType.BIND_PHONE].includes(viewType)
  );

  const [showJoinFail, setShowJoinFail] = useState('');

  if (showJoinFail) {
    return (
      <Container hideHeaderLink hideFooter showScoreRule={false}>
        <JoinSpaceError
          errorType={TeamInviteStatus.MEMBER_FULL}
          joinType="guest"
          errorMsg={showJoinFail}
          onClick={() => {
            locationToUrl(`/`);
          }}
          btnText={`返回 ${VITE_PRODUCT_NAME}`}
        />
      </Container>
    );
  }
  if (inviteTeamCode) {
    return <InviteTeam inviteTeamCode={inviteTeamCode} />;
  }

  if (isLoading) {
    return <LoadingContainer />;
  }

  return (
    <Container hideFooter={hideFooter} showScoreRule={viewType === ViewType.COMPLETE}>
      <Login
        defaultTitle={`👋欢迎来到 ${PRODUCT_NAME}`}
        titleClassName="text-h1"
        autoLogin={true}
        styleType={isBuildIn() ? 'build-in-pc' : 'default'}
        quickRegister={quickRegister}
        inviteCode={urlInviteCode}
        promotionChannel={getPromotionChannel()}
        hideThirdPartLogin={__PRIVATE__}
        inviteCodeRequired={__PRIVATE__}
        onRegister={(user) => {
          updateCurrentUser(user);
          kolActivity(true);
        }}
        onSuccess={async (user, _) => {
          // if (!opt?.isAuto) {
          // 如果是手动登录，先清理下本地缓存，但不要清理index db，防止备份的日志数据丢失
          // localStorage.clear();
          // }
          setLocalStorage(USER_ID, user.uuid);
          clearPromotionChannel();

          if (cooperateCode && pageCode) {
            setIsLoading(true);
            // 没有权限的情况下再继续
            const res = await request.infra.joinDocByInvitation.raw(cooperateCode);
            if (res.code !== 200) {
              setShowJoinFail(res.msg);
              // message.error(res.msg);
              // await sleep(2000);
              // locationToUrl(`/${pageCode}`);
              return;
            }
            message.success('加入成功！你已经是页面协作者了');
            await sleep(2000);
            locationToUrl(`/${pageCode}`);
            return;
          }

          if (user.spaceViews.length === 0 && !quickRegister) {
            locationToUrl(ViewPath.create, true);
            return;
          }

          void jumpQueryCallback();
        }}
        onStep={(viewType) => {
          setViewType(viewType);
        }}
      />
    </Container>
  );
};

const InviteTeam: FC<{ inviteTeamCode: string }> = ({ inviteTeamCode }) => {
  const jumpQueryCallback = useJumpQueryCallback();
  const getQuery = useGetQuery();
  const urlInviteCode = getQuery(SearchParams.inviteCode);
  const [showFooter, setShowFooter] = useState(true);

  useEffect(() => {
    if (inviteTeamCode) {
      void request.infra.getMyUserInfo.raw().then((res) => {
        if (res.code === 200) {
          updateCurrentUser(res.data);
        }
      });
    }
  }, [inviteTeamCode]);

  return (
    <Container hideHeaderLink={true} isJoinTeam={true} hideFooter={!showFooter}>
      <TeamInviteWeb
        setShowFooter={setShowFooter}
        styleType={isBuildIn() ? 'build-in-pc' : 'default'}
        promotionChannel={getPromotionChannel()}
        inviteCode={urlInviteCode}
        teamInviteCode={inviteTeamCode}
        hideThirdPartLogin={__PRIVATE__}
        goBackHome={() => {
          locationToUrl(getUrlWithoutDomain());
        }}
        onLogin={(user) => {
          updateCurrentUser(user);
          setLocalStorage(USER_ID, user.uuid);
          kolActivity(!isNumber(user.occupation));
        }}
        onSuccess={async (spaceId) => {
          await fetchSpaces();
          switchSpaceViewIdBySpaceId(spaceId);
          void jumpQueryCallback();
        }}
      />
    </Container>
  );
};
